import ReactECharts from "echarts-for-react";
import { useState, useEffect } from "react";
import axios from "axios";
import RotateDevice from "../../../components/RotateDevice/RotateDevice";
import "./Dettaglio.css";
import Lf23Loader from "../../../components/Lf23Loader";

// Palette di colori solidi ben distinti tra loro per la pie chart.
// Evito gradienti che, quando le fette sono piccole, vengono percepiti tutti
// come variazioni dello stesso colore.
const PIE_COLORS = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
    '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16',
    '#F97316', '#6366F1', '#14B8A6', '#A855F7',
    '#FACC15', '#22D3EE', '#F472B6', '#94A3B8'
];

const formatEuro = (v) => {
    if (v === null || v === undefined || isNaN(v)) return '-';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(v);
};

const formatNumber = (v, decimals = 2) => {
    if (v === null || v === undefined || isNaN(v)) return '-';
    return new Intl.NumberFormat('it-IT', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(v);
};

const Dettaglio = ({ filters, loading: parentLoading = false }) => {
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    const [costoKmTarga, setCostoKmTarga] = useState([]);
    const [costiCausale, setCostiCausale] = useState([]);
    const [costoKmSettore, setCostoKmSettore] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);

    // Cross-filter: { type: 'causale'|'targa'|'settore', value: 'X' } | null.
    // Clic su una fetta/barra: settato. Re-clic stesso elemento: deselezionato.
    // Il filtro si applica agli ALTRI 2 grafici (riduce i dati), mentre il
    // grafico cliccato mantiene visibili tutti gli elementi con visual
    // feedback (opacity sfumata sui non-selezionati).
    const [crossFilter, setCrossFilter] = useState(null);

    // stati fullscreen
    const [isFullscreenTarga, setIsFullscreenTarga] = useState(false);
    const [isFullscreenCausale, setIsFullscreenCausale] = useState(false);
    const [isFullscreenSettore, setIsFullscreenSettore] = useState(false);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                setIsFullscreenTarga(false);
                setIsFullscreenCausale(false);
                setIsFullscreenSettore(false);
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, []);

    // Quando cambiano i filtri sidebar, resetto il cross-filter (il dataset
    // base cambia e l'elemento selezionato potrebbe non esistere più).
    useEffect(() => {
        setCrossFilter(null);
    }, [filters]);

    const buildFilterParams = ({ includeCausaleSel = true, includeTargaSel = true, includeSettoreSel = true } = {}) => {
        const params = new URLSearchParams();
        if (filters?.fromDate) params.append('dataInizio', filters.fromDate);
        if (filters?.toDate) params.append('dataFine', filters.toDate);
        if (filters?.tipologiaVeicolo?.length > 0) {
            params.append('tipologia', filters.tipologiaVeicolo.map(t => t.value).join(','));
        }
        if (filters?.sottotipologia?.length > 0) {
            params.append('sottotipologia', filters.sottotipologia.map(s => s.value).join(','));
        }
        if (filters?.targa?.length > 0) {
            params.append('targa', filters.targa.map(t => t.value).join(','));
        }
        if (filters?.cantiere?.length > 0) {
            params.append('cantiere', filters.cantiere.map(c => c.value).join(','));
        }
        if (filters?.settore?.length > 0) {
            params.append('settore', filters.settore.map(s => s.value).join(','));
        }
        if (filters?.commessa?.length > 0) {
            params.append('commessa', filters.commessa.map(c => c.value).join(','));
        }
        if (filters?.operatore?.length > 0) {
            params.append('operatore', filters.operatore.map(o => o.value).join(','));
        }
        // Cross-filter di selezione click. Ogni grafico esclude il proprio
        // tipo per non auto-filtrarsi.
        if (crossFilter) {
            if (crossFilter.type === 'causale' && includeCausaleSel) {
                params.append('causaleSel', crossFilter.value);
            }
            if (crossFilter.type === 'targa' && includeTargaSel) {
                params.append('targaSel', crossFilter.value);
            }
            if (crossFilter.type === 'settore' && includeSettoreSel) {
                params.append('settoreSel', crossFilter.value);
            }
        }
        return params.toString();
    };

    useEffect(() => {
        const fetchData = async () => {
            setDataLoading(true);
            try {
                // Ogni endpoint riceve i cross-filter ECCETTO il proprio tipo
                const qsTarga = buildFilterParams({ includeTargaSel: false });
                const qsCausale = buildFilterParams({ includeCausaleSel: false });
                const qsSettore = buildFilterParams({ includeSettoreSel: false });
                const [resTarga, resCausale, resSettore] = await Promise.all([
                    axios.get(`${API_URL}/operation-automezzi/dettaglio/costo-km-targa${qsTarga ? `?${qsTarga}` : ''}`),
                    axios.get(`${API_URL}/operation-automezzi/dettaglio/costi-causale${qsCausale ? `?${qsCausale}` : ''}`),
                    axios.get(`${API_URL}/operation-automezzi/dettaglio/costo-km-settore${qsSettore ? `?${qsSettore}` : ''}`),
                ]);
                setCostoKmTarga(Array.isArray(resTarga.data) ? resTarga.data : []);
                setCostiCausale(Array.isArray(resCausale.data) ? resCausale.data : []);
                setCostoKmSettore(Array.isArray(resSettore.data) ? resSettore.data : []);
            } catch (error) {
                console.error("Errore nel caricamento dei dati Dettaglio:", error);
            } finally {
                setDataLoading(false);
            }
        };
        fetchData();
    }, [API_URL, filters, crossFilter]);

    // Loader full-screen solo al primo caricamento: se ci sono già dati a
    // schermo (refetch da cross-filter o cambio sidebar), li manteniamo
    // visibili per evitare lo "scatto in alto" causato dall'unmount dei
    // grafici durante il fetch.
    const hasAnyData = costoKmTarga.length > 0
        || costiCausale.length > 0
        || costoKmSettore.length > 0;
    const isLoading = parentLoading || (dataLoading && !hasAnyData);
    const isEmpty = !isLoading && !dataLoading && !hasAnyData;

    // ----- Handler click cross-filter -----
    const toggleCrossFilter = (type, value) => {
        if (crossFilter?.type === type && crossFilter.value === value) {
            setCrossFilter(null);
        } else {
            setCrossFilter({ type, value });
        }
    };

    const handlePieClick = (params) => toggleCrossFilter('causale', params.name);
    const handleTargaBarClick = (params) => {
        const item = costoKmTarga[params.dataIndex];
        if (item) toggleCrossFilter('targa', item.targa);
    };
    const handleSettoreBarClick = (params) => {
        const item = costoKmSettore[params.dataIndex];
        if (item) toggleCrossFilter('settore', item.settore);
    };

    // ----- Grafico 1: Costo al Km per Targa (bar orizzontale) -----
    const targaOpacityFor = (targa) =>
        crossFilter?.type === 'targa' && crossFilter.value !== targa ? 0.3 : 1;

    const targaChartOption = {
        title: { show: false },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params) => {
                const p = params[0];
                const item = costoKmTarga[p.dataIndex];
                if (!item) return '';
                return `<strong>${item.targa}</strong><br/>` +
                    `Costo al Km: <strong>${formatNumber(item.costo_km, 3)} €/km</strong><br/>` +
                    `Importo totale: ${formatEuro(item.importo_totale)}<br/>` +
                    `Distanza: ${formatNumber(item.dist_km, 1)} km`;
            }
        },
        grid: { left: '3%', right: '10%', bottom: '3%', top: '2%', containLabel: true },
        xAxis: {
            type: 'value',
            name: '€/km',
            nameLocation: 'end',
            nameGap: 5,
            axisLabel: { formatter: (v) => formatNumber(v, 2) }
        },
        yAxis: {
            type: 'category',
            data: costoKmTarga.map(d => d.targa),
            inverse: true,
            axisLabel: { fontSize: 10, width: 80, overflow: 'truncate' }
        },
        series: [{
            name: 'Costo al Km',
            type: 'bar',
            data: costoKmTarga.map(d => ({
                value: d.costo_km,
                itemStyle: {
                    color: '#2E6DB1',
                    borderRadius: [0, 4, 4, 0],
                    opacity: targaOpacityFor(d.targa),
                }
            })),
            label: {
                show: true,
                position: 'right',
                formatter: (params) => formatNumber(params.value, 2),
                fontSize: 10
            },
            barMaxWidth: 18
        }]
    };

    // ----- Grafico 2: Pie causali -----
    const causaleOpacityFor = (name) =>
        crossFilter?.type === 'causale' && crossFilter.value !== name ? 0.3 : 1;

    const causaleChartOption = {
        color: PIE_COLORS,
        tooltip: {
            trigger: 'item',
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            borderColor: 'transparent',
            borderRadius: 10,
            padding: [10, 14],
            textStyle: { color: '#F8FAFC', fontSize: 12, fontWeight: 500 },
            extraCssText: 'box-shadow: 0 10px 25px rgba(0,0,0,0.25); backdrop-filter: blur(8px);',
            formatter: (params) => `
                <div style="font-weight:700;font-size:13px;margin-bottom:4px;">${params.name}</div>
                <div>${formatEuro(params.value)} · ${params.percent}%</div>
            `
        },
        legend: {
            type: 'plain',
            orient: 'horizontal',
            bottom: 10,
            left: 'center',
            itemWidth: 10,
            itemHeight: 10,
            itemGap: 12,
            icon: 'circle',
            textStyle: { fontSize: 11, color: '#475569', fontWeight: 500 }
        },
        series: [{
            name: 'Causale',
            type: 'pie',
            radius: ['42%', '62%'],
            center: ['50%', '50%'],
            top: 10,
            bottom: 60,
            avoidLabelOverlap: true,
            padAngle: 2,
            itemStyle: {
                borderRadius: 8,
                borderColor: '#ffffff',
                borderWidth: 3,
                shadowBlur: 10,
                shadowColor: 'rgba(15, 23, 42, 0.12)'
            },
            label: {
                show: true,
                position: 'outside',
                formatter: '{b|{b}}\n{p|{d}%}',
                rich: {
                    b: { color: '#1E293B', fontSize: 11, fontWeight: 600, lineHeight: 16 },
                    p: { color: '#3B82F6', fontSize: 11, fontWeight: 700, lineHeight: 14 }
                }
            },
            labelLine: {
                show: true,
                length: 8,
                length2: 10,
                smooth: true,
                lineStyle: { color: '#CBD5E1', width: 1 }
            },
            emphasis: {
                scale: true,
                scaleSize: 10,
                itemStyle: { shadowBlur: 25, shadowColor: 'rgba(59, 130, 246, 0.45)' },
                label: { fontWeight: 700 }
            },
            data: costiCausale.map(c => ({
                name: c.causale,
                value: c.importo_totale,
                itemStyle: { opacity: causaleOpacityFor(c.causale) }
            })),
            animationType: 'scale',
            animationEasing: 'elasticOut',
            animationDuration: 900,
            animationDelay: (idx) => idx * 60
        }]
    };

    // ----- Grafico 3: Costo al Km per Settore (bar verticale) -----
    const settoreOpacityFor = (settore) =>
        crossFilter?.type === 'settore' && crossFilter.value !== settore ? 0.3 : 1;

    const settoreChartOption = {
        title: { show: false },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params) => {
                const p = params[0];
                const item = costoKmSettore[p.dataIndex];
                if (!item) return '';
                return `<strong>${item.settore}</strong><br/>` +
                    `Costo al Km: <strong>${formatNumber(item.costo_km, 3)} €/km</strong><br/>` +
                    `Importo totale: ${formatEuro(item.importo_totale)}<br/>` +
                    `Distanza: ${formatNumber(item.dist_km, 1)} km`;
            }
        },
        grid: { left: 50, right: 30, bottom: 90, top: 50, containLabel: false },
        xAxis: {
            type: 'category',
            data: costoKmSettore.map(d => d.settore),
            axisLabel: { rotate: 35, fontSize: 11, interval: 0, margin: 10 }
        },
        yAxis: {
            type: 'value',
            name: '€/km',
            nameLocation: 'end',
            nameGap: 20,
            axisLabel: { formatter: (v) => formatNumber(v, 2) }
        },
        series: [{
            name: 'Costo al Km',
            type: 'bar',
            data: costoKmSettore.map(d => ({
                value: d.costo_km,
                itemStyle: {
                    color: '#2E6DB1',
                    borderRadius: [4, 4, 0, 0],
                    opacity: settoreOpacityFor(d.settore),
                }
            })),
            label: {
                show: true,
                position: 'top',
                formatter: (params) => formatNumber(params.value, 2),
                fontSize: 11,
                fontWeight: 'bold'
            },
            barMaxWidth: 60
        }]
    };

    const FullscreenIcon = ({ active }) => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {active ? (
                <>
                    <polyline points="4 14 10 14 10 20"></polyline>
                    <polyline points="20 10 14 10 14 4"></polyline>
                    <line x1="14" y1="10" x2="21" y2="3"></line>
                    <line x1="3" y1="21" x2="10" y2="14"></line>
                </>
            ) : (
                <>
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <polyline points="9 21 3 21 3 15"></polyline>
                    <line x1="21" y1="3" x2="14" y2="10"></line>
                    <line x1="3" y1="21" x2="10" y2="14"></line>
                </>
            )}
        </svg>
    );

    // Altezza dinamica del grafico targhe: minimo 400px, cresce con le righe
    const targhaChartHeight = `${Math.max(400, costoKmTarga.length * 22)}px`;

    return (
        <div className="operazioni-automezzi-dettaglio">
            {isLoading ? (
                <div className="dettaglio-loader-wrapper">
                    <Lf23Loader />
                </div>
            ) : isEmpty ? (
                <div className="dettaglio-empty-wrapper">
                    <div className="oper-hr-empty-state">
                        <div className="oper-hr-empty-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                <line x1="9" y1="9" x2="15" y2="15"></line>
                                <line x1="15" y1="9" x2="9" y2="15"></line>
                            </svg>
                        </div>
                        <h4 className="oper-hr-empty-title">Nessun dato disponibile</h4>
                        <p className="oper-hr-empty-text">
                            Non ci sono dati per i filtri selezionati. Prova a modificare i filtri.
                        </p>
                    </div>
                </div>
            ) : (
                <>
                    {/* Riga 1, colonna 1: Costo al Km per Targa */}
                    <div className={`dettaglio-chart-card dettaglio-chart-targa${isFullscreenTarga ? ' fullscreen-chart' : ''}`}>
                        <RotateDevice active={isFullscreenTarga} />
                        <div className="dettaglio-chart-header automezzi-gen-chart-header">
                            <span className="charts-title dettaglio-chart-title">Costo al Km per Targa</span>
                            <button
                                className="automezzi-gen-fullscreen-btn"
                                onClick={() => setIsFullscreenTarga(!isFullscreenTarga)}
                                title={isFullscreenTarga ? 'Esci da tutto schermo' : 'Tutto schermo'}
                            >
                                <FullscreenIcon active={isFullscreenTarga} />
                            </button>
                        </div>
                        {costoKmTarga.length > 0 ? (
                            <ReactECharts
                                option={targaChartOption}
                                style={{ height: targhaChartHeight, width: '100%' }}
                                notMerge={true}
                                onEvents={{ click: handleTargaBarClick }}
                            />
                        ) : (
                            <div className="dettaglio-no-data">Nessun dato</div>
                        )}
                    </div>

                    {/* Riga 1, colonna 2: Ripartizione costi per Targa (pie su causale) */}
                    <div className={`dettaglio-chart-card dettaglio-chart-causale${isFullscreenCausale ? ' fullscreen-chart' : ''}`}>
                        <RotateDevice active={isFullscreenCausale} />
                        <div className="dettaglio-chart-header automezzi-gen-chart-header">
                            <span className="charts-title dettaglio-chart-title">Ripartizione costi per Targa</span>
                            <button
                                className="automezzi-gen-fullscreen-btn"
                                onClick={() => setIsFullscreenCausale(!isFullscreenCausale)}
                                title={isFullscreenCausale ? 'Esci da tutto schermo' : 'Tutto schermo'}
                            >
                                <FullscreenIcon active={isFullscreenCausale} />
                            </button>
                        </div>
                        {costiCausale.length > 0 ? (
                            <ReactECharts
                                option={causaleChartOption}
                                style={{ height: '100%', width: '100%', minHeight: '450px' }}
                                notMerge={true}
                                onEvents={{ click: handlePieClick }}
                            />
                        ) : (
                            <div className="dettaglio-no-data">Nessun dato</div>
                        )}
                    </div>

                    {/* Riga 2, colonna 1: Costo al Km per Settore e Commessa */}
                    <div className={`dettaglio-chart-card dettaglio-chart-settore${isFullscreenSettore ? ' fullscreen-chart' : ''}`}>
                        <RotateDevice active={isFullscreenSettore} />
                        <div className="dettaglio-chart-header automezzi-gen-chart-header">
                            <span className="charts-title dettaglio-chart-title">Costo al Km per Settore e Commessa</span>
                            <button
                                className="automezzi-gen-fullscreen-btn"
                                onClick={() => setIsFullscreenSettore(!isFullscreenSettore)}
                                title={isFullscreenSettore ? 'Esci da tutto schermo' : 'Tutto schermo'}
                            >
                                <FullscreenIcon active={isFullscreenSettore} />
                            </button>
                        </div>
                        {costoKmSettore.length > 0 ? (
                            <ReactECharts
                                option={settoreChartOption}
                                style={{ height: '100%', width: '100%', minHeight: '420px' }}
                                notMerge={true}
                                onEvents={{ click: handleSettoreBarClick }}
                            />
                        ) : (
                            <div className="dettaglio-no-data">Nessun dato</div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default Dettaglio;
